import { USER_IDENTIFIER_METADATA_DISPLAY_TEXT } from '@modules/common/constants';
import { mapSentimentTypeToColor } from '@modules/feedback/components/FeedbackDetails/utils';
import { useFeedbackOnFeedbackStore } from '@modules/feedback/store';
import {
  getMappedFeedbackSourceMetadataFields,
  getSentimentPrediction,
} from '@modules/feedback/utils';
import { getIconForIntegrationType } from '@modules/integrationsV2/utils';
import {
  Box,
  capitalize,
  Chip,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { ObjectType } from 'generated/graphql';
import { ReactNode } from 'react';
import { DATE_FORMAT } from 'v2/core/date/date';
import { Feedback, FeedbackLabel } from 'v2/core/entities/feedback';

const IMPORTANT_METADATA_FIELDS_TO_SHOW = 2;

interface FeedbackCardMetadataPreviewProps {
  feedback: Feedback;
  sentimentLabel: FeedbackLabel | undefined;
}

export const FeedbackCardMetadataPreview = ({
  feedback,
  sentimentLabel,
}: FeedbackCardMetadataPreviewProps) => {
  const editedRecords = useFeedbackOnFeedbackStore(
    ({ editedRecords }) => editedRecords,
  );
  const editedRecord = editedRecords.find(
    (editedRecord) => editedRecord.id === feedback.id,
  );

  const { language, sourceCreatedAt } = feedback;

  const metadataFields = getMappedFeedbackSourceMetadataFields({
    metadataFields: feedback.sourceV2?.metadata,
  });

  const getMetadataItemText = ({
    key,
    values,
    icon,
    shouldShowKey = false,
  }: {
    key: string;
    values: string[];
    icon?: JSX.Element | null;
    shouldShowKey?: boolean;
  }) => {
    const firstMetadataValue = values[0];
    const remainingMetadataValues = values.slice(1);

    return (
      <Tooltip arrow title={`${key}: ${values.join(', ')}`} placement="top">
        <Stack direction="row" alignItems="center" spacing={0.5}>
          {icon}
          <Typography variant="caption" noWrap>
            {shouldShowKey && (
              <Box
                component="span"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {key}:{' '}
              </Box>
            )}
            {firstMetadataValue}
            {remainingMetadataValues.length > 0 &&
              ` (+${remainingMetadataValues.length})`}
          </Typography>
        </Stack>
      </Tooltip>
    );
  };

  const metadataItemsToDisplay = (() => {
    const metadataItems: ReactNode[] = [];

    if (sentimentLabel) {
      const editedSentiment = getSentimentPrediction(editedRecord)?.prediction;
      const sentimentName = capitalize(
        editedSentiment ?? sentimentLabel?.displayName,
      );
      metadataItems.push(
        <Tooltip
          arrow
          placement="top"
          title={`User Sentiment: ${sentimentName}`}
        >
          <Chip
            size="small"
            label={<Typography variant="caption">{sentimentName}</Typography>}
            sx={{
              bgcolor: mapSentimentTypeToColor(sentimentName).color,
              color: 'white',
            }}
            variant="filled"
          />
        </Tooltip>,
      );
    }

    metadataItems.push(
      getMetadataItemText({
        key: 'Language',
        values: [language.name],
      }),
    );

    if (sourceCreatedAt) {
      metadataItems.push(
        getMetadataItemText({
          key: 'Created At',
          values: [dayjs(sourceCreatedAt).tz().format(DATE_FORMAT)],
        }),
      );
    }

    const userIdentifierMetadataField = metadataFields.find(
      (metadata) =>
        metadata.key === USER_IDENTIFIER_METADATA_DISPLAY_TEXT &&
        metadata.objectType === ObjectType.User,
    );

    if (userIdentifierMetadataField) {
      metadataItems.push(
        getMetadataItemText({
          key: userIdentifierMetadataField.key,
          values: userIdentifierMetadataField.values,
          shouldShowKey: true,
        }),
      );
    }

    const importantMetadataFields =
      metadataFields.filter((metadata) => metadata.isImportant) ?? [];

    if (importantMetadataFields.length > 0) {
      importantMetadataFields
        .slice(0, IMPORTANT_METADATA_FIELDS_TO_SHOW)
        .forEach((metadata) => {
          metadataItems.push(
            getMetadataItemText({
              key: metadata.key,
              values: metadata.values,
              shouldShowKey: true,
            }),
          );
        });
    }

    const referencedUserMetadataField = metadataFields.find(
      (metadata) =>
        metadata.isReference && metadata.objectType === ObjectType.User,
    );

    if (referencedUserMetadataField) {
      metadataItems.push(
        getMetadataItemText({
          key: referencedUserMetadataField.key,
          values: referencedUserMetadataField.values,
          shouldShowKey: true,
          icon: getIconForIntegrationType({
            integrationType: referencedUserMetadataField.objectType.toString(),
            sx: {
              fontSize: (theme) => theme.typography.body2.fontSize,
            },
          }),
        }),
      );
    }

    const referencedAccountMetadataField = metadataFields.find(
      (metadata) =>
        metadata.isReference && metadata.objectType === ObjectType.Account,
    );

    if (referencedAccountMetadataField) {
      metadataItems.push(
        getMetadataItemText({
          key: referencedAccountMetadataField.key,
          values: referencedAccountMetadataField.values,
          shouldShowKey: true,
          icon: getIconForIntegrationType({
            integrationType:
              referencedAccountMetadataField.objectType.toString(),
            sx: {
              fontSize: (theme) => theme.typography.body2.fontSize,
            },
          }),
        }),
      );
    }

    return metadataItems;
  })();

  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      {metadataItemsToDisplay.map((metadataItem, index) => {
        return (
          <Stack key={index} direction="row" alignItems="center" spacing={1}>
            <Box>{metadataItem}</Box>
            {index !== metadataItemsToDisplay.length - 1 && (
              <Typography color="text.disabled" variant="body2">
                •
              </Typography>
            )}
          </Stack>
        );
      })}
    </Stack>
  );
};
